import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Save, History, Sparkles, Bug, Wrench, Zap, Tag } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface VersionChange {
  category: string;
  description: string;
}

interface AppVersion {
  id: string;
  version_number: string;
  release_date: string;
  changes: VersionChange[];
}

const categoryOptions = [
  { value: "Feature", label: "Feature", icon: Sparkles },
  { value: "Bugfix", label: "Bugfix", icon: Bug },
  { value: "Improvement", label: "Improvement", icon: Wrench },
  { value: "Initial", label: "Initial", icon: Zap },
];

const categoryColors: Record<string, string> = {
  Feature: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  Bugfix: "bg-red-500/10 text-red-500 border-red-500/20",
  Improvement: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  Initial: "bg-primary/10 text-primary border-primary/20",
};

const VersionManagement = () => {
  const [versions, setVersions] = useState<AppVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // New version form state
  const [newVersion, setNewVersion] = useState("");
  const [newChanges, setNewChanges] = useState<VersionChange[]>([
    { category: "Feature", description: "" },
  ]);

  useEffect(() => {
    loadVersions();
  }, []);

  const loadVersions = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("app_versions")
      .select("*")
      .order("release_date", { ascending: false });

    if (!error && data) {
      const typedVersions = data.map((v) => ({
        ...v,
        changes: Array.isArray(v.changes)
          ? (v.changes as unknown as VersionChange[])
          : [],
      }));
      setVersions(typedVersions);
      
      // Suggest next version number
      if (typedVersions.length > 0) {
        const currentVersion = typedVersions[0].version_number;
        const parts = currentVersion.split(".");
        if (parts.length === 3) {
          const patch = parseInt(parts[2]) + 1;
          setNewVersion(`${parts[0]}.${parts[1]}.${patch}`);
        }
      }
    }
    setIsLoading(false);
  };

  const addChange = () => {
    setNewChanges([...newChanges, { category: "Feature", description: "" }]);
  };

  const removeChange = (index: number) => {
    if (newChanges.length > 1) {
      setNewChanges(newChanges.filter((_, i) => i !== index));
    }
  };

  const updateChange = (index: number, field: keyof VersionChange, value: string) => {
    const updated = [...newChanges];
    updated[index] = { ...updated[index], [field]: value };
    setNewChanges(updated);
  };

  const handleSaveVersion = async () => {
    if (!newVersion.trim()) {
      toast.error("Bitte geben Sie eine Versionsnummer ein");
      return;
    }

    const validChanges = newChanges.filter((c) => c.description.trim());
    if (validChanges.length === 0) {
      toast.error("Bitte fügen Sie mindestens eine Änderung hinzu");
      return;
    }

    // Check if version already exists
    const exists = versions.some((v) => v.version_number === newVersion.trim());
    if (exists) {
      toast.error("Diese Versionsnummer existiert bereits");
      return;
    }

    setIsSaving(true);
    const { error } = await supabase.from("app_versions").insert([{
      version_number: newVersion.trim(),
      changes: JSON.parse(JSON.stringify(validChanges)),
    }]);

    if (error) {
      toast.error("Fehler beim Speichern der Version");
      console.error(error);
    } else {
      toast.success("Version erfolgreich erstellt");
      setNewChanges([{ category: "Feature", description: "" }]);
      loadVersions();
    }
    setIsSaving(false);
  };

  const getCategoryIcon = (category: string) => {
    const option = categoryOptions.find((o) => o.value === category);
    if (option) {
      const Icon = option.icon;
      return <Icon className="h-3.5 w-3.5" />;
    }
    return <Tag className="h-3.5 w-3.5" />;
  };

  const getCategoryColor = (category: string) => {
    return categoryColors[category] || "bg-muted text-muted-foreground border-border";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* New Version Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Neue Version erstellen
          </CardTitle>
          <CardDescription>
            Fügen Sie eine neue Version mit Änderungen hinzu
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="version">Versionsnummer</Label>
              <Input
                id="version"
                placeholder="z.B. 1.2.0"
                value={newVersion}
                onChange={(e) => setNewVersion(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label>Änderungen</Label>
            {newChanges.map((change, index) => (
              <div key={index} className="flex gap-2 items-start">
                <Select
                  value={change.category}
                  onValueChange={(value) => updateChange(index, "category", value)}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <option.icon className="h-4 w-4" />
                          {option.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea
                  placeholder="Beschreibung der Änderung..."
                  value={change.description}
                  onChange={(e) => updateChange(index, "description", e.target.value)}
                  className="flex-1 min-h-[40px] resize-none"
                  rows={1}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeChange(index)}
                  disabled={newChanges.length === 1}
                  className="shrink-0"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addChange}>
              <Plus className="h-4 w-4 mr-2" />
              Weitere Änderung
            </Button>
          </div>

          <Button onClick={handleSaveVersion} disabled={isSaving} className="w-full sm:w-auto">
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? "Wird gespeichert..." : "Version speichern"}
          </Button>
        </CardContent>
      </Card>

      {/* Existing Versions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Bisherige Versionen
          </CardTitle>
          <CardDescription>
            Übersicht aller veröffentlichten Versionen
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Version</TableHead>
                  <TableHead className="w-[150px]">Datum</TableHead>
                  <TableHead>Änderungen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.map((version, vIndex) => (
                  <TableRow key={version.id}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        v{version.version_number}
                      </Badge>
                      {vIndex === 0 && (
                        <Badge className="ml-2 bg-primary/10 text-primary border-primary/20">
                          Aktuell
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(version.release_date), "dd.MM.yyyy", { locale: de })}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {version.changes.map((change, cIndex) => (
                          <div key={cIndex} className="flex items-center gap-2 text-sm">
                            <Badge
                              variant="outline"
                              className={cn(
                                "flex items-center gap-1 text-xs px-2 py-0.5 shrink-0",
                                getCategoryColor(change.category)
                              )}
                            >
                              {getCategoryIcon(change.category)}
                              {change.category}
                            </Badge>
                            <span className="text-foreground">{change.description}</span>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {versions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                      Noch keine Versionen vorhanden
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VersionManagement;
